import { useEffect, useState } from 'react';
import { Download, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import {
  Button,
  Checkbox,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@staff-app/ui';
import {
  errorText,
  installVersionValue,
  versionCategory,
  versionEntries,
  versionKey,
  versionsUrl,
} from '../lib/device-commands';
import {
  TestingConfirm,
  TestingDialogFrame,
  type TestingCommandDialogProps,
} from './TestingDialogFrame';

/**
 * More › Install OptiSigns Versions — port of scio3 `InstallVersionDialog`. The version list is a
 * direct CDN fetch (android / ProPlayer JSON, keyed by model → category → typeBuild); the send is
 * addCommandExecution MDM_INSTALL_OPTISIGNS_VERSION with `JSON.stringify({...entry, isDisableAutoUpdate})`.
 * Confirmed before sending — it reinstalls the player on a real device.
 */
export function TestingInstallVersionDialog({
  open,
  detail,
  deviceName,
  commands,
  onClose,
  onDone,
}: TestingCommandDialogProps) {
  const category = versionCategory(detail);
  const isAndroid = category === 'android';
  const key = versionKey(category, detail);
  const url = versionsUrl(category);

  const [cdn, setCdn] = useState<{ data: unknown; error: string | null } | null>(null);
  const [picked, setPicked] = useState<string | null>(null);
  const [pin, setPin] = useState(Boolean(detail.feature?.isDisableAutoUpdate));
  const [confirming, setConfirming] = useState(false);
  const [sending, setSending] = useState(false);

  // One CDN read per open (a static public JSON — not an API, so no Apollo).
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    fetch(url)
      .then(async (res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return (await res.json()) as unknown;
      })
      .then((data) => !cancelled && setCdn({ data, error: null }))
      .catch((err: Error) => !cancelled && setCdn({ data: null, error: err.message }));
    return () => {
      cancelled = true;
    };
  }, [open, url]);

  const entries = versionEntries(cdn?.data, key);
  const installed = detail.localAppVersion ?? null;
  const selected = picked ?? (entries.some((e) => e.version === installed) ? installed : null);
  const entry = entries.find((e) => e.version === selected) ?? null;
  const alreadyInstalled = !!selected && selected === installed;

  const install = async () => {
    setConfirming(false);
    if (!entry) return;
    setSending(true);
    try {
      await commands.addCommandExecution(
        'MDM_INSTALL_OPTISIGNS_VERSION',
        installVersionValue(entry, isAndroid ? pin : false),
      );
      toast.success(`Install ${entry.version} sent`);
      onDone();
      onClose();
    } catch (err) {
      toast.error(errorText(err, 'Failed to install version'));
    } finally {
      setSending(false);
    }
  };

  let disabledTooltip: string | undefined;
  if (!entry) disabledTooltip = 'Select a version';
  else if (alreadyInstalled) disabledTooltip = 'This version is already installed';

  return (
    <TestingDialogFrame
      open={open}
      onClose={onClose}
      title={'Install OptiSigns Versions'}
      icon={Download}
      testId="testing-install-version-dialog"
      footer={
        <Button
          variant="destructive"
          onClick={() => setConfirming(true)}
          disabled={!entry || alreadyInstalled || sending}
          disabledTooltip={sending ? undefined : disabledTooltip}
          data-testid="testing-install-version-submit"
        >
          {sending ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : null}
          {'Install'}
        </Button>
      }
    >
      <p className="text-sm text-muted-foreground">
        {installed ? `Installed: v${installed}. ` : ''}
        {`Versions from the ${isAndroid ? 'Android' : 'ProPlayer'} channel (${key}).`}
      </p>
      {!cdn ? (
        <div className="flex justify-center py-4">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" aria-hidden />
        </div>
      ) : cdn.error ? (
        <p className="text-sm text-destructive">{`Failed to load versions: ${cdn.error}`}</p>
      ) : (
        <div className="space-y-2">
          <Label htmlFor="testing-install-version-select">{'Version'}</Label>
          <Select value={selected ?? undefined} onValueChange={setPicked}>
            <SelectTrigger id="testing-install-version-select" className="cursor-pointer">
              <SelectValue placeholder={entries.length ? 'Select version…' : 'No versions listed'} />
            </SelectTrigger>
            <SelectContent className="max-h-72">
              {entries.map((e) => (
                <SelectItem key={e.version} value={e.version}>
                  {e.version}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}
      {isAndroid ? (
        <div className="flex items-center gap-2">
          <Checkbox
            id="testing-install-version-pin"
            checked={pin}
            onCheckedChange={(c) => setPin(c === true)}
          />
          <Label htmlFor="testing-install-version-pin" className="cursor-pointer text-sm font-normal">
            {'Pin this version (disable auto update)'}
          </Label>
        </div>
      ) : null}

      <TestingConfirm
        open={confirming}
        title={'Install OptiSigns version'}
        description={`Install v${entry?.version ?? ''} on ${deviceName}? The player app reinstalls and restarts.`}
        confirmLabel={'Install'}
        onConfirm={() => void install()}
        onCancel={() => setConfirming(false)}
      />
    </TestingDialogFrame>
  );
}
